import React, { useEffect, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { GETUsers, GetAdresses } from '../../services/users';
import { AdminCreateAmcLead } from '../../services/requests';
import { capitalizeWords } from '../../utils/helper';
import useSnackbar from '../../hooks/useSnackbar';
import {
  darkDialogPaperProps,
  darkFieldSx,
  darkItemCardSx,
  darkMenuPaperSx,
  darkTitleSx,
} from '../../components/common/darkFormStyles';

interface Props {
  open: boolean;
  onClose: () => void;
  onSaved?: () => void;
}

const AC_TYPES = ['Split', 'Window', 'Cassette', 'VRV/VRF', 'Ducted', 'Tower', 'Chiller'];

const initialAcDetail = { acType: '', quantity: '1' };

const AddAmcRequest = ({ open, onClose, onSaved }: Props) => {
  const [users, setUsers] = useState<any[]>([]);
  const [userSearch, setUserSearch] = useState('');
  const [usersLoading, setUsersLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState<any | null>(null);
  const [addresses, setAddresses] = useState<any[]>([]);
  const [addressId, setAddressId] = useState('');
  const [addressesLoading, setAddressesLoading] = useState(false);
  const [place, setPlace] = useState('residential');
  const [acDetails, setAcDetails] = useState<any[]>([{ ...initialAcDetail }]);
  const [comment, setComment] = useState('');
  const [saving, setSaving] = useState(false);
  const { showSnackbar, SnackbarComponent } = useSnackbar();

  const userId = selectedUser?._id || '';

  useEffect(() => {
    if (!open) return;
    const delay = setTimeout(() => {
      setUsersLoading(true);
      GETUsers({ page: 1, limit: 50, searchQuery: userSearch.trim() })
        .then((response) => setUsers(response.data || []))
        .finally(() => setUsersLoading(false));
    }, 400);

    return () => clearTimeout(delay);
  }, [userSearch, open]);

  useEffect(() => {
    if (!userId) {
      setAddresses([]);
      setAddressId('');
      return;
    }
    setAddressesLoading(true);
    GetAdresses(userId)
      .then((response) => setAddresses(response.data || []))
      .finally(() => setAddressesLoading(false));
  }, [userId]);

  const updateAcDetail = (index: number, field: string, value: string) => {
    setAcDetails((prev) =>
      prev.map((item, itemIndex) => (itemIndex === index ? { ...item, [field]: value } : item)),
    );
  };

  const addAcDetail = () => setAcDetails((prev) => [...prev, { ...initialAcDetail }]);
  const removeAcDetail = (index: number) =>
    setAcDetails((prev) => prev.filter((_, itemIndex) => itemIndex !== index));

  const totalQuantity = acDetails.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);

  const resetForm = () => {
    setSelectedUser(null);
    setUserSearch('');
    setAddressId('');
    setPlace('residential');
    setAcDetails([{ ...initialAcDetail }]);
    setComment('');
  };

  const handleClose = () => {
    if (saving) return;
    onClose();
  };

  const handleSubmit = async () => {
    if (!userId || !addressId || !place) {
      showSnackbar('Customer, address and place type are required', 'error');
      return;
    }
    if (acDetails.some((item) => !item.acType || !item.quantity || Number(item.quantity) < 1)) {
      showSnackbar('Enter AC type and quantity for every AC line', 'error');
      return;
    }

    try {
      setSaving(true);
      await AdminCreateAmcLead({
        user_id: userId,
        addressId,
        place,
        acDetails: acDetails.map((item) => ({
          acType: item.acType,
          quantity: Number(item.quantity) || 1,
        })),
        quantity: totalQuantity,
        comment,
      });
      showSnackbar('AMC request created', 'success');
      onSaved?.();
      resetForm();
      onClose();
    } catch (error) {
      showSnackbar(error instanceof Error ? error.message : 'Failed to create request', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth PaperProps={darkDialogPaperProps}>
      {SnackbarComponent}

      <DialogTitle sx={darkTitleSx}>
        Add AMC Request
        <IconButton onClick={handleClose} sx={{ color: '#9e9e9e' }} aria-label="Close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Divider sx={{ borderColor: '#2a2a2a' }} />

      <DialogContent sx={{ py: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <Autocomplete
            options={users}
            value={selectedUser}
            loading={usersLoading}
            onChange={(_event, newValue) => setSelectedUser(newValue)}
            onInputChange={(_event, newInputValue) => setUserSearch(newInputValue)}
            getOptionLabel={(option) =>
              `${capitalizeWords(option.name || '') || option.phoneNumber} - ${option.phoneNumber}`
            }
            isOptionEqualToValue={(option, value) => option._id === value._id}
            sx={darkFieldSx}
            slotProps={{ paper: darkMenuPaperSx }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Customer"
                placeholder="Search by name or phone"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {usersLoading ? <CircularProgress size={16} sx={{ color: '#E31E25' }} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />

          <FormControl fullWidth disabled={!userId || addressesLoading} sx={darkFieldSx}>
            <InputLabel>Address</InputLabel>
            <Select
              value={addressId}
              label="Address"
              onChange={(event) => setAddressId(event.target.value)}
              MenuProps={{ PaperProps: darkMenuPaperSx }}
            >
              {addresses.map((address) => (
                <MenuItem key={address._id} value={address._id}>
                  {[address.house, address.street, address.city, address.zipcode].filter(Boolean).join(', ')}
                </MenuItem>
              ))}
              {!addressesLoading && userId && addresses.length === 0 && (
                <MenuItem disabled value="">
                  No saved addresses for this customer
                </MenuItem>
              )}
            </Select>
          </FormControl>

          <FormControl fullWidth sx={darkFieldSx}>
            <InputLabel>Place Type</InputLabel>
            <Select
              value={place}
              label="Place Type"
              onChange={(event) => setPlace(event.target.value)}
              MenuProps={{ PaperProps: darkMenuPaperSx }}
            >
              <MenuItem value="residential">Residential</MenuItem>
              <MenuItem value="commercial">Commercial</MenuItem>
            </Select>
          </FormControl>

          <Typography sx={{ color: '#9e9e9e', fontSize: 13, fontWeight: 600 }}>AC Details</Typography>

          {acDetails.map((item, index) => (
            <Box key={index} sx={darkItemCardSx}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Typography sx={{ color: '#fff', fontWeight: 600 }}>AC {index + 1}</Typography>
                {acDetails.length > 1 && (
                  <IconButton size="small" onClick={() => removeAcDetail(index)} sx={{ color: '#9e9e9e' }}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 2 }}>
                <FormControl fullWidth sx={darkFieldSx}>
                  <InputLabel>AC Type</InputLabel>
                  <Select
                    value={item.acType}
                    label="AC Type"
                    onChange={(event) => updateAcDetail(index, 'acType', event.target.value)}
                    MenuProps={{ PaperProps: darkMenuPaperSx }}
                  >
                    {AC_TYPES.map((type) => (
                      <MenuItem key={type} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  label="Quantity"
                  type="number"
                  value={item.quantity}
                  onChange={(event) => updateAcDetail(index, 'quantity', event.target.value)}
                  onWheel={(event) => (event.target as HTMLElement).blur()}
                  sx={darkFieldSx}
                />
              </Box>
            </Box>
          ))}

          <Button
            variant="outlined"
            onClick={addAcDetail}
            sx={{
              alignSelf: 'flex-start',
              color: '#E31E25',
              borderColor: '#E31E25',
              borderRadius: '10px',
              '&:hover': { borderColor: '#B71C1C', backgroundColor: 'rgba(227, 30, 37, 0.08)' },
            }}
          >
            + Add Another AC
          </Button>

          <TextField
            label="Comment"
            value={comment}
            onChange={(event) => setComment(event.target.value)}
            multiline
            minRows={2}
            sx={darkFieldSx}
          />
        </Box>
      </DialogContent>

      <Divider sx={{ borderColor: '#2a2a2a' }} />

      <DialogActions sx={{ p: 2.5 }}>
        <Button onClick={handleClose} disabled={saving} sx={{ color: '#9e9e9e' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : null}
          sx={{
            background: '#E31E25',
            color: '#fff',
            px: 4,
            py: 1.1,
            borderRadius: '10px',
            fontWeight: 600,
            '&:hover': { background: '#C62828' },
            '&.Mui-disabled': { background: '#7a2a2a', color: '#ddd' },
          }}
        >
          {saving ? 'Saving…' : 'Create Request'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddAmcRequest;
